/**
 * ChangeReasonDialog - Dialog für Änderungsbegründung
 * 
 * Wird verwendet wenn:
 * 1. Backend 400 mit Begründungspflicht antwortet
 * 2. Client erkennt vorab dass ein Grund nötig ist (fremder Eintrag / eingereichte Woche)
 */
import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const MIN_REASON_LENGTH = 5;

export const ChangeReasonDialog = ({
  open,
  onOpenChange,
  title = "Änderungsgrund erforderlich",
  description = "Für diese Änderung ist eine Begründung erforderlich.",
  reasonHint = null,
  onConfirm,
  onCancel,
  isLoading = false,
  confirmButtonText = "Änderung speichern",
  cancelButtonText = "Abbrechen",
  isDelete = false,
}) => {
  const [reason, setReason] = useState('');

  // Reset reason when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  const isReasonValid = reason.trim().length >= MIN_REASON_LENGTH;

  const handleConfirm = () => {
    if (isReasonValid) {
      onConfirm(reason.trim());
    }
  };

  const handleCancel = () => {
    setReason('');
    onCancel?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${isDelete ? 'text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
            {title}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {reasonHint && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-300">{reasonHint}</p>
            </div>
          )}

          {isDelete && (
            <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
              Der Eintrag wird als gelöscht markiert und aus allen Auswertungen entfernt. 
              Aus Nachweisgründen bleibt er im Änderungsprotokoll erhalten und kann von 
              einem Administrator wiederhergestellt werden.
            </div>
          )}

          <div>
            <label className="text-sm text-muted-foreground mb-2 block">
              Begründung <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Bitte geben Sie eine Begründung ein (mind. 5 Zeichen)..."
              className="bg-input border-border text-foreground min-h-[100px] resize-none"
              data-testid="change-reason-input"
              autoFocus
            />
            <div className="flex justify-between mt-2">
              <p className={`text-xs ${reason.length >= MIN_REASON_LENGTH ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
                {reason.length} / {MIN_REASON_LENGTH} Zeichen (minimum)
              </p>
              {reason.length > 0 && reason.length < MIN_REASON_LENGTH && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Noch {MIN_REASON_LENGTH - reason.length} Zeichen erforderlich
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="text-muted-foreground"
            disabled={isLoading}
          >
            {cancelButtonText}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!isReasonValid || isLoading}
            className={isDelete ? "bg-red-500 hover:bg-red-600" : "bg-indigo-500 hover:bg-indigo-600"}
            data-testid="confirm-change-reason-btn"
          >
            {isLoading ? 'Wird gespeichert...' : confirmButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Hook für Änderungsbegründungs-Logik
 * Prüft ob ein Grund erforderlich ist und managed den Dialog-State
 */
export const useChangeReason = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [reasonConfig, setReasonConfig] = useState({
    title: "Änderungsgrund erforderlich",
    description: "Für diese Änderung ist eine Begründung erforderlich.",
    reasonHint: null,
    isDelete: false,
  });

  /**
   * Prüft ob ein Änderungsgrund erforderlich ist
   * @param {Object} entry - Der zu ändernde Eintrag
   * @param {Object} currentUser - Der aktuell angemeldete Benutzer
   * @param {Object} approval - Optional: Genehmigungsstatus der Woche
   * @returns {boolean} true wenn Grund erforderlich
   */
  const isReasonRequired = (entry, currentUser, approval = null) => {
    // Regel 1: Fremder Eintrag (user_id != eigene ID)
    if (entry?.user_id && entry.user_id !== currentUser?.id) {
      return true;
    }

    // Regel 2: Bereits eingereichte oder genehmigte Woche
    if (approval) {
      const status = approval.status;
      if (status === 'pending' || status === 'approved') {
        return true;
      }
    }

    // Regel 3: Eintrag hat bereits approved Status
    if (entry?.status === 'approved') {
      return true;
    }

    return false;
  };

  /**
   * Startet eine Aktion die möglicherweise einen Grund erfordert
   * @param {Function} action - Die auszuführende Aktion (wird mit reason als Parameter aufgerufen)
   * @param {Object} entry - Der betroffene Eintrag
   * @param {Object} currentUser - Der aktuelle Benutzer
   * @param {Object} approval - Optional: Genehmigungsstatus
   * @param {Object} config - Dialog-Konfiguration
   */
  const executeWithReason = (action, entry, currentUser, approval = null, config = {}) => {
    const needsReason = isReasonRequired(entry, currentUser, approval);

    if (needsReason) {
      setPendingAction(() => action);
      
      let hint = null;
      if (entry?.user_id && entry.user_id !== currentUser?.id) {
        hint = "Sie bearbeiten einen Eintrag eines anderen Mitarbeiters.";
      } else if (approval?.status === 'pending') {
        hint = "Die Woche wurde bereits zur Genehmigung eingereicht.";
      } else if (approval?.status === 'approved') {
        hint = "Die Woche wurde bereits genehmigt.";
      } else if (entry?.status === 'approved') {
        hint = "Dieser Eintrag wurde bereits genehmigt.";
      }

      setReasonConfig({
        title: config.title || (config.isDelete ? "Löschgrund erforderlich" : "Änderungsgrund erforderlich"),
        description: config.description || "Für diese Änderung ist eine Begründung erforderlich.",
        reasonHint: hint,
        isDelete: config.isDelete || false,
      });
      
      setDialogOpen(true);
    } else {
      // Kein Grund erforderlich, direkt ausführen
      action(null);
    }
  };

  const handleConfirm = (reason) => {
    if (pendingAction) {
      pendingAction(reason);
      setPendingAction(null);
    }
    setDialogOpen(false);
  };

  const handleCancel = () => {
    setPendingAction(null);
    setDialogOpen(false);
  };

  return {
    dialogOpen,
    setDialogOpen,
    reasonConfig,
    executeWithReason,
    handleConfirm,
    handleCancel,
    isReasonRequired,
  };
};

export default ChangeReasonDialog;
